"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

type DynamicInitFunction = (...args: unknown[]) => unknown;

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

const DEFAULT_RETRY_ATTEMPTS = 12;
const DEFAULT_RETRY_INTERVAL_MS = 150;
const DEFAULT_CALENDLY_RETRY_ATTEMPTS = 40;
const INIT_DONE_PATH_ATTR = "data-cms-embed-init-path";
const INIT_LOCK_ATTR = "data-cms-embed-init-lock";
const SCAN_INTERVAL_MS = 250;
const SCAN_WINDOW_MS = 10000;
const SUCCESS_CHECK_INTERVAL_MS = 50;
const DEFAULT_SUCCESS_TIMEOUT_MS = 2000;

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const getIntAttr = (
  element: HTMLElement,
  attribute: string,
  fallback: number,
): number => {
  const raw = element.getAttribute(attribute)?.trim();
  if (!raw) return fallback;

  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;

  return parsed;
};

const getBoolAttr = (
  element: HTMLElement,
  attribute: string,
  fallback = false,
): boolean => {
  const raw = element.getAttribute(attribute);
  if (raw === null) return fallback;

  const normalized = raw.trim().toLowerCase();
  if (normalized.length === 0) return true;
  if (normalized === "true" || normalized === "1" || normalized === "yes") {
    return true;
  }
  if (normalized === "false" || normalized === "0" || normalized === "no") {
    return false;
  }

  return fallback;
};

const resolveGlobalFunction = (
  path: string,
): DynamicInitFunction | undefined => {
  const segments = path
    .split(".")
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length === 0) return undefined;

  let current: unknown = window;

  for (const segment of segments) {
    if (
      typeof current !== "object" &&
      typeof current !== "function" &&
      current !== null
    ) {
      return undefined;
    }

    current = (current as Record<string, unknown>)[segment];
  }

  return typeof current === "function"
    ? (current as DynamicInitFunction)
    : undefined;
};

const parseArgsTemplate = (raw: string): JsonValue | undefined => {
  try {
    return JSON.parse(raw) as JsonValue;
  } catch {
    return undefined;
  }
};

const resolveTemplateValue = (
  value: JsonValue,
  element: HTMLElement,
  pathname: string,
): unknown => {
  if (typeof value === "string") {
    if (value === "$element") return element;
    if (value === "$pathname") return pathname;
    if (value.startsWith("@attr:")) {
      return element.getAttribute(value.slice("@attr:".length)) ?? undefined;
    }

    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => resolveTemplateValue(item, element, pathname));
  }

  if (value && typeof value === "object") {
    const resolved: Record<string, unknown> = {};
    for (const [key, nestedValue] of Object.entries(value)) {
      resolved[key] = resolveTemplateValue(nestedValue, element, pathname);
    }
    return resolved;
  }

  return value;
};

const getInitArgs = (element: HTMLElement, pathname: string): unknown[] => {
  const raw = element.getAttribute("data-embed-init-args")?.trim();
  if (!raw) return [];

  const parsed = parseArgsTemplate(raw);
  if (parsed === undefined) return [];

  const resolved = resolveTemplateValue(parsed, element, pathname);
  return Array.isArray(resolved) ? resolved : [resolved];
};

const isCalendlyWidget = (element: HTMLElement): boolean =>
  element.classList.contains("calendly-inline-widget") &&
  Boolean(element.getAttribute("data-url")?.trim());

const isCalendlyInlineInitPath = (path: string): boolean =>
  path.trim() === "Calendly.initInlineWidget";

const getResolvedInitArgs = (
  element: HTMLElement,
  pathname: string,
  initPath: string,
): unknown[] => {
  const explicitArgs = getInitArgs(element, pathname);
  if (explicitArgs.length > 0) return explicitArgs;

  if (isCalendlyInlineInitPath(initPath) && isCalendlyWidget(element)) {
    const url = element.getAttribute("data-url")?.trim();
    if (!url) return [];

    return [{ url, parentElement: element }];
  }

  return [];
};

const hasDomContent = (element: HTMLElement): boolean =>
  element.childElementCount > 0 || element.innerHTML.trim().length > 0;

const waitForInitSuccess = async (
  element: HTMLElement,
  selector: string | undefined,
  timeoutMs: number,
  isCancelled: () => boolean,
): Promise<boolean> => {
  if (!selector) {
    return true;
  }

  const startedAt = Date.now();
  while (Date.now() - startedAt <= timeoutMs) {
    if (isCancelled() || !element.isConnected) return false;
    try {
      if (element.querySelector(selector)) return true;
    } catch {
      return false;
    }
    await wait(SUCCESS_CHECK_INTERVAL_MS);
  }

  return false;
};

const initElement = async (
  element: HTMLElement,
  pathname: string,
  isCancelled: () => boolean,
): Promise<void> => {
  const lastInitPath = element.getAttribute(INIT_DONE_PATH_ATTR);
  if (lastInitPath === pathname) return;
  if (element.getAttribute(INIT_LOCK_ATTR) === "true") return;

  const explicitInitPath = element.getAttribute("data-embed-init-fn")?.trim();
  const inferredInitPath = isCalendlyWidget(element)
    ? "Calendly.initInlineWidget"
    : undefined;
  const initPath = explicitInitPath || inferredInitPath;
  if (!initPath) return;

  const calendlyTarget = isCalendlyWidget(element);
  const hasCustomAttempts = element.hasAttribute("data-embed-init-attempts");
  const hasCustomReset = element.hasAttribute("data-embed-init-reset");
  const hasCustomSuccessSelector = element.hasAttribute(
    "data-embed-init-success-selector",
  );

  const attempts = getIntAttr(
    element,
    "data-embed-init-attempts",
    hasCustomAttempts
      ? DEFAULT_RETRY_ATTEMPTS
      : calendlyTarget
        ? DEFAULT_CALENDLY_RETRY_ATTEMPTS
        : DEFAULT_RETRY_ATTEMPTS,
  );
  const interval = getIntAttr(
    element,
    "data-embed-init-interval",
    DEFAULT_RETRY_INTERVAL_MS,
  );
  const resetBeforeInit = hasCustomReset
    ? getBoolAttr(element, "data-embed-init-reset", false)
    : calendlyTarget;
  const successSelector =
    element.getAttribute("data-embed-init-success-selector")?.trim() ||
    (!hasCustomSuccessSelector && calendlyTarget ? "iframe" : undefined);
  const successTimeout = getIntAttr(
    element,
    "data-embed-init-success-timeout",
    DEFAULT_SUCCESS_TIMEOUT_MS,
  );

  element.setAttribute(INIT_LOCK_ATTR, "true");

  try {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (isCancelled() || !element.isConnected) return;

      const initFn = resolveGlobalFunction(initPath);
      if (initFn) {
        try {
          if (resetBeforeInit) {
            element.innerHTML = "";
          }

          const args = getResolvedInitArgs(element, pathname, initPath);
          await Promise.resolve(initFn(...args));

          if (successSelector) {
            const isSuccessful = await waitForInitSuccess(
              element,
              successSelector,
              successTimeout,
              isCancelled,
            );

            if (!isSuccessful) {
              await wait(interval);
              continue;
            }
          } else if (!hasDomContent(element)) {
            await wait(interval);
            continue;
          }

          element.setAttribute(INIT_DONE_PATH_ATTR, pathname);
          return;
        } catch {
          // Retry in case provider runtime is present but not ready yet.
        }
      }

      await wait(interval);
    }
  } finally {
    element.removeAttribute(INIT_LOCK_ATTR);
  }
};

const scanAndInit = async (
  pathname: string,
  isCancelled: () => boolean,
): Promise<void> => {
  const targetsSet = new Set<HTMLElement>();
  for (const target of document.querySelectorAll<HTMLElement>(
    "[data-embed-init-fn], .calendly-inline-widget[data-url]",
  )) {
    targetsSet.add(target);
  }
  const targets = Array.from(targetsSet);

  for (const target of targets) {
    await initElement(target, pathname, isCancelled);
  }
};

export function EmbedScriptInit() {
  const pathname = usePathname();

  useEffect(() => {
    let cancelled = false;
    let scanning = false;
    let queued = false;

    const currentPath = pathname ?? "";

    const runScan = () => {
      if (cancelled) return;
      if (scanning) {
        queued = true;
        return;
      }

      scanning = true;
      void (async () => {
        try {
          await scanAndInit(currentPath, () => cancelled);
        } finally {
          scanning = false;
          if (queued) {
            queued = false;
            runScan();
          }
        }
      })();
    };

    runScan();

    const observer =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => {
            runScan();
          })
        : null;

    if (observer && document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (Date.now() - startedAt > SCAN_WINDOW_MS) {
        window.clearInterval(timer);
        return;
      }

      runScan();
    }, SCAN_INTERVAL_MS);

    return () => {
      cancelled = true;
      observer?.disconnect();
      window.clearInterval(timer);
    };
  }, [pathname]);

  return null;
}
